import { useContent } from "../hooks/useContent";
import { useInView } from "../hooks/useInView";
import { CalendarClock, Truck, Sparkles, Zap, ArrowRight } from "lucide-react";

const iconMap = {
  calendarClock: CalendarClock,
  truck: Truck,
  sparkles: Sparkles,
  zap: Zap,
};

export const HowItWorks = () => {
  const [ref, isInView] = useInView();
  const { getSection } = useContent();
  const howItWorksSteps = getSection("howItWorks") || [];

  return (
    <section
      id="how-it-works"
      className="py-16 lg:py-20 bg-slate-50/70 relative overflow-hidden"
    >
      {/* Decorative */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-sky-50/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12" ref={ref}>
          <span className="inline-block text-sm font-semibold text-sky-600 tracking-wider uppercase mb-4">
            How It Works
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mb-5">
            Laundry Made
            <br />
            Effortlessly Simple
          </h2>
          <p className="text-lg text-slate-500 leading-relaxed">
            Four simple steps to fresh, clean clothes delivered to your doorstep.
            It's that easy.
          </p>
        </div>

        {/* Steps */}
        <div
          className={`grid md:grid-cols-2 lg:grid-cols-4 gap-8 transition-all duration-700 ${
            isInView
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-8"
          }`}
        >
          {howItWorksSteps.map((step, i) => {
            const Icon = iconMap[step.iconKey];
            return (
              <div
                key={step.step}
                className="relative group"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                {/* Connector line */}
                {i < howItWorksSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-[60%] w-[calc(100%-20%)] h-px border-t-2 border-dashed border-sky-200" />
                )}

                <div className="relative bg-white rounded-2xl p-7 border border-slate-100 hover:border-sky-200 hover:shadow-lg hover:shadow-sky-900/5 transition-all duration-300">
                  {/* Step number */}
                  <div className="absolute -top-4 -left-2 w-8 h-8 rounded-full bg-sky-600 text-white text-sm font-bold flex items-center justify-center shadow-lg shadow-sky-600/30">
                    {step.step}
                  </div>

                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-50 to-sky-100/60 border border-sky-100 flex items-center justify-center mb-5 group-hover:scale-105 transition-transform duration-300">
                    <Icon className="w-7 h-7 text-sky-600" />
                  </div>

                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <button
            onClick={() => {
              const el = document.querySelector("#contact");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center gap-2 text-sky-600 font-semibold hover:text-sky-700 transition-colors group"
          >
            Get started in 30 seconds
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};
